/**
 * `ChampionshipApplication.position` is a free-form `String` in the schema that
 * in practice holds `PlayerPosition` values ('defender' | 'forward' |
 * 'goalkeeper' | 'midfielder' — modules/teams/domain/enums/player-position.ts).
 *
 * Why a local table rather than i18n `t('player.position.*')`
 * (src/locales/ru.json): the render pages are headless and produce a PNG for a
 * Russian-language product — there is no user locale in scope, and routing this
 * through i18n would make the exported image depend on whatever UI language the
 * operator happened to have set, which is not what an author laying out a
 * template expects to see. Every other label in the manager module
 * (PLACEHOLDER_KEY_REGISTRY, TEMPLATE_TYPE_REGISTRY) hardcodes its Russian the
 * same way. Capitalized (ru.json is lowercase) because a placeholder renders
 * this as a standalone value, not mid-sentence.
 *
 * Why not import modules/teams' PlayerPosition enum: cross-module domain imports
 * are avoided by convention — see the StageType note in standings-render-api.ts.
 */
const PLAYER_POSITION_LABELS: Readonly<Record<string, string>> = {
  defender: 'Защитник',
  forward: 'Нападающий',
  goalkeeper: 'Вратарь',
  midfielder: 'Полузащитник',
};

/**
 * Unknown values pass through unmapped rather than becoming undefined: the
 * author then sees exactly what the backend sent instead of a silently blank
 * slot, which is the difference between "this position is new" and "this
 * placeholder is broken".
 */
export function playerPositionLabel(position: string | null | undefined): string | undefined {
  if (!position) return undefined;
  const trimmed = position.trim();
  if (!trimmed) return undefined;
  return PLAYER_POSITION_LABELS[trimmed.toLowerCase()] ?? trimmed;
}
